import type { ColumnSizingInfoState, Header } from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'

interface ResizableHeaderProps<T> {
  header: Header<T, unknown>
  columnSizingInfo: ColumnSizingInfoState
  onContextMenu: (e: React.MouseEvent, columnId: string) => void
}

export function ResizableHeader<T>({
  header,
  columnSizingInfo,
  onContextMenu,
}: ResizableHeaderProps<T>) {
  // Calculate width including resize delta for smooth visual feedback
  const isResizing = columnSizingInfo.isResizingColumn === header.column.id
  const deltaOffset = columnSizingInfo.deltaOffset ?? 0
  const width = isResizing ? header.getSize() + deltaOffset : header.getSize()

  return (
    <th
      key={header.id}
      style={{ width, position: 'relative' }}
      className="text-muted-foreground px-3 py-3 text-left text-xs font-medium tracking-wider uppercase select-none"
      onContextMenu={(e) => onContextMenu(e, header.column.id)}
    >
      <div className="flex items-center gap-1">
        {header.isPlaceholder ? null : header.column.getCanSort() ? (
          <button
            type="button"
            onClick={() => header.column.toggleSorting()}
            className="hover:text-foreground flex items-center gap-1 transition-colors"
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
            {header.column.getIsSorted() === 'asc' ? (
              <ArrowUp size={14} className="text-primary" />
            ) : header.column.getIsSorted() === 'desc' ? (
              <ArrowDown size={14} className="text-primary" />
            ) : (
              <ArrowUpDown size={14} className="opacity-40" />
            )}
          </button>
        ) : (
          flexRender(header.column.columnDef.header, header.getContext())
        )}
      </div>
      {/* Resize handle */}
      {header.column.getCanResize() && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={`absolute top-0 right-0 flex h-full w-3 cursor-col-resize items-center justify-center touch-none select-none ${
            header.column.getIsResizing()
              ? 'bg-primary/20'
              : 'hover:bg-primary/10'
          }`}
        >
          <div
            className={`h-4 w-px ${
              header.column.getIsResizing()
                ? 'bg-primary'
                : 'bg-border group-hover:bg-muted-foreground/50'
            }`}
          />
        </div>
      )}
    </th>
  )
}
